import { AI_BUTTON_STYLE_PROPERTIES, AI_CARD_STYLE_PROPERTIES, AI_EDITABLE_STYLE_PROPERTIES, AI_GLOBAL_BUTTON_STYLE_IDS, AI_GLOBAL_CARD_STYLE_IDS, AI_GLOBAL_ICON_STYLE_IDS, AI_ICON_STYLE_PROPERTIES, AI_GLOBAL_TEXT_STYLE_IDS, AI_TEXT_STYLE_PROPERTIES, } from './aiEditor';
/**
 * Every visual compact route is the same request in different clothes: given a
 * set of targets and a whitelist of properties, return the properties to change.
 * Describing them as data instead of as six near-identical prompt builders,
 * normalizers and rejection branches is what keeps a new route cheap to add.
 *
 * Two families:
 * - `tokens`  — brand scope patches global theme tokens; selection scope falls
 *               back to local styles on the selected element.
 * - `element` — always patches the elements listed in the request context.
 */
/** Colour properties a Header/Footer container may change. */
export const AI_CHROME_STYLE_PROPERTIES = [
    'background', 'backgroundColor', 'color', 'borderColor',
];
/**
 * The editor's colour controls only parse `#rrggbb`, `rgb()` and `var()`. A named
 * colour renders fine on canvas but leaves every swatch showing its fallback, so
 * the model has to answer in a form the UI can read back.
 */
const COLOUR_FORMAT_RULE = 'Para cualquier color usa hex de 6 digitos (#rrggbb) o rgb(). Nunca nombres CSS como "red", ni hex de 3 digitos.';
/**
 * References like "el anaranjado que ya tenemos" only resolve if the palette
 * travels with the request and the model is told to reach for it first.
 */
const PALETTE_REUSE_RULE = 'Si la solicitud menciona un color que "ya existe", "el de la marca" o lo describe por matiz, elige el token de themeColors cuyo valor mas se acerque y usa su var(--wb-<token>) en lugar de inventar un hex nuevo.';
export const AI_STYLE_SCOPES = [
    {
        id: 'card',
        executionMode: 'card_style',
        properties: AI_CARD_STYLE_PROPERTIES,
        targets: 'single',
        responsive: false,
        tokens: {
            ids: AI_GLOBAL_CARD_STYLE_IDS,
            operationType: 'patch_card_style',
            requestKey: 'cardStyles',
        },
        label: { one: 'la tarjeta seleccionada', many: 'los tres estilos globales de tarjeta' },
        rules: [
            COLOUR_FORMAT_RULE,
            PALETTE_REUSE_RULE,
            'No uses color de texto, tipografia, hover, animacion, transition, transform, width ni height.',
            'Conserva las diferencias intencionales entre los estilos.',
        ],
    },
    {
        id: 'button',
        executionMode: 'button_style',
        properties: AI_BUTTON_STYLE_PROPERTIES,
        targets: 'single',
        responsive: false,
        tokens: {
            ids: AI_GLOBAL_BUTTON_STYLE_IDS,
            operationType: 'patch_button_style',
            requestKey: 'buttonStyles',
        },
        label: { one: 'el boton seleccionado', many: 'los dos estilos globales de boton' },
        rules: [
            COLOUR_FORMAT_RULE,
            PALETTE_REUSE_RULE,
            'Jerarquia fija: button-primary usa fondo var(--wb-accent); button-secondary usa fondo transparente con borde y texto var(--wb-accent); button-link no se envia y conserva solo texto var(--wb-accent).',
            'No propongas otros colores para botones.',
            'No cambies href, destino, texto, iconos, enlaces ni comportamiento.',
        ],
    },
    {
        id: 'icon',
        executionMode: 'icon_style',
        properties: AI_ICON_STYLE_PROPERTIES,
        targets: 'single',
        responsive: false,
        tokens: {
            ids: AI_GLOBAL_ICON_STYLE_IDS,
            operationType: 'patch_icon_style',
            requestKey: 'iconStyles',
            allowsIconSize: true,
        },
        label: { one: 'el icono seleccionado', many: 'los tres tokens globales de icono' },
        rules: [
            COLOUR_FORMAT_RULE,
            PALETTE_REUSE_RULE, 'No cambies el simbolo ni el iconName; solo su apariencia.'
        ],
    },
    {
        id: 'text',
        executionMode: 'text_style',
        properties: AI_TEXT_STYLE_PROPERTIES,
        targets: 'single',
        responsive: false,
        tokens: {
            ids: AI_GLOBAL_TEXT_STYLE_IDS,
            operationType: 'patch_text_style',
            requestKey: 'textStyles',
        },
        label: { one: 'el texto seleccionado', many: 'los estilos de texto (h1-h6, parrafo y small)' },
        rules: [
            COLOUR_FORMAT_RULE,
            PALETTE_REUSE_RULE,
            'Cambia solo las propiedades pedidas; no toques tamanos ni pesos si la solicitud habla de color.',
            'Conserva la jerarquia entre niveles: h1 nunca debe quedar igual o menor que h2.',
            'No cambies fondos ni bordes: estos tokens solo controlan el texto.',
        ],
    },
    {
        id: 'chrome',
        executionMode: 'chrome_style',
        properties: AI_CHROME_STYLE_PROPERTIES,
        targets: 'listed',
        responsive: true,
        label: { one: 'el contenedor indicado', many: 'los contenedores Header/Footer indicados' },
        rules: [
            COLOUR_FORMAT_RULE,
            PALETTE_REUSE_RULE,
            'Si esta permitido modificar estos colores aunque el target sea navegacion o componente de sistema.',
            'No cambies textos, enlaces, href, hijos, layout, posicion, estructura, comportamiento ni visibilidad.',
        ],
    },
    {
        id: 'element',
        executionMode: 'element_style',
        properties: AI_EDITABLE_STYLE_PROPERTIES,
        targets: 'listed',
        responsive: false,
        label: { one: 'el elemento seleccionado', many: 'los elementos seleccionados' },
        rules: [
            COLOUR_FORMAT_RULE,
            PALETTE_REUSE_RULE,
            'No cambies textos, contenido, enlaces, href, iconos, hijos ni estructura: solo estilos.',
            'Usa unicamente los elementId recibidos; no inventes ninguno.',
        ],
    },
];
export const AI_STYLE_SCOPE_BY_MODE = new Map(AI_STYLE_SCOPES.map((scope) => [scope.executionMode, scope]));
export function aiStyleScopeForMode(executionMode) {
    return AI_STYLE_SCOPE_BY_MODE.get(executionMode);
}
/**
 * What a user can name, and the properties that would satisfy it. A compact
 * route matching the *target* ("las tarjetas") is not enough: if the request
 * also names something the route's whitelist cannot express, taking the route
 * would drop that half of the request in silence.
 */
const PROPERTY_FAMILIES = [
    {
        pattern: /\b(letra|letras|fuente|tipograf|font|bold|negrita|cursiva|italic|peso|interlineado|mayuscula|minuscula)\w*/,
        properties: [
            'fontFamily', 'fontWeight', 'fontSize', 'fontStyle',
            'lineHeight', 'letterSpacing', 'textTransform', 'textDecoration',
        ],
    },
    {
        pattern: /\b(color|colores|paleta|tono|tonos|fondo|background)\w*/,
        properties: ['color', 'background', 'backgroundColor', 'borderColor'],
    },
    {
        pattern: /\b(padding|margen|margin|espacio|espaciado|aire|relleno)\w*/,
        properties: ['padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft', 'margin', 'gap'],
    },
    {
        pattern: /\b(redonde|radio|radius|esquina)\w*/,
        properties: ['borderRadius'],
    },
    {
        pattern: /\b(sombra|shadow|elevaci)\w*/,
        properties: ['boxShadow'],
    },
    {
        pattern: /\b(columna|columnas|fila|filas|grid|flex|apila|apilad|alinea|centra)\w*/,
        properties: ['display', 'flexDirection', 'gridTemplateColumns', 'gap', 'alignItems', 'justifyContent'],
    },
];
/**
 * Things no visual route can do at all. A request that mixes these with a style
 * change belongs on the standard route, which can combine both.
 */
const NON_STYLE_INTENT = /\b(traduc|reescrib|redact|corrig|ortograf|agrega|anade|inserta|borra|elimina|duplica|mueve)\w*/;
/**
 * True when this scope can satisfy everything the message asks for. Routes call
 * it before claiming a request, so a compound ask falls to the standard route
 * instead of silently losing the part the scope cannot express.
 */
export function aiStyleScopeCanExpress(scope, message) {
    const normalized = message.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    if (NON_STYLE_INTENT.test(normalized))
        return false;
    const available = new Set(scope.properties);
    return PROPERTY_FAMILIES.every((family) => (!family.pattern.test(normalized)
        || family.properties.some((property) => available.has(property))));
}
//# sourceMappingURL=aiStyleScopes.js.map